import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Loader2, CheckCircle2, Copy } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

/**
 * BookingDialog — lightweight booking inquiry form opened from a model profile.
 *
 * Submits to POST /api/bookings, which routes the inquiry to the management
 * inbox via Resend + stores it in the admin dashboard "Bookings" panel.
 */
const PROJECT_TYPES = [
  { value: "editorial",  label: "Editorial" },
  { value: "campaign",   label: "Campaign" },
  { value: "runway",     label: "Runway / show" },
  { value: "lookbook",   label: "Lookbook" },
  { value: "commercial", label: "Commercial" },
  { value: "film",       label: "Film / motion" },
  { value: "other",      label: "Other" },
];

export default function BookingDialog({ model, children }) {
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null); // { reference } | null
  const [form, setForm] = useState({
    client_name: "", company: "", email: "", phone: "",
    project_type: "", shoot_dates: "", location: "", budget_range: "",
    message: "",
  });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const reset = () => {
    setDone(null);
    setForm({
      client_name: "", company: "", email: "", phone: "",
      project_type: "", shoot_dates: "", location: "", budget_range: "",
      message: "",
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.client_name || !form.email || !form.message) {
      toast.error("Name, email and brief are required");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        model_id: model.id,
        client_name: form.client_name.trim(),
        company: form.company.trim() || null,
        email: form.email.trim(),
        phone: form.phone.trim() || null,
        project_type: form.project_type || null,
        shoot_dates: form.shoot_dates.trim() || null,
        location: form.location.trim() || null,
        budget_range: form.budget_range.trim() || null,
        message: form.message.trim(),
      };
      const r = await api.post("/bookings", payload);
      setDone({ reference: r.data.reference });
      toast.success("Inquiry sent — we'll be in touch within 48h");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't send. Try again or email info@egemodels.com");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        setOpen(v);
        if (!v) setTimeout(reset, 300); // wait for close animation before clearing state
      }}
    >
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent
        data-testid="booking-dialog"
        className="bg-white border-black/15 rounded-none max-w-2xl text-[#0A0A0A] max-h-[90vh] overflow-y-auto"
      >
        {done ? (
          <>
            {/* Required by Radix for a11y even when visually hidden */}
            <DialogHeader className="sr-only">
              <DialogTitle>Booking inquiry sent</DialogTitle>
              <DialogDescription>Your booking inquiry has been received. We will reply within 48 hours.</DialogDescription>
            </DialogHeader>
            <SuccessView reference={done.reference} model={model} onClose={() => setOpen(false)} />
          </>
        ) : (
          <>
            <DialogHeader>
              <p className="label-overline text-zinc-500">Booking inquiry</p>
              <DialogTitle className="font-serif text-3xl font-normal tracking-tight">
                Book {model.name}<span className="text-zinc-400">.</span>
              </DialogTitle>
              <DialogDescription className="text-zinc-500 text-sm leading-relaxed">
                We answer every inquiry within 48 hours. For non-booking enquiries, email{" "}
                <a className="link-underline" href="mailto:info@egemodels.com">info@egemodels.com</a>.
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={submit} className="grid grid-cols-2 gap-4 mt-2">
              <BField label="Your name *">
                <Input data-testid="bk-name" value={form.client_name} onChange={(e) => set("client_name", e.target.value)} placeholder="Jane Doe" />
              </BField>
              <BField label="Company / agency">
                <Input data-testid="bk-company" value={form.company} onChange={(e) => set("company", e.target.value)} placeholder="Optional" />
              </BField>
              <BField label="Email *">
                <Input data-testid="bk-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="you@brand.com" />
              </BField>
              <BField label="Phone / WhatsApp">
                <Input data-testid="bk-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="+31 …" />
              </BField>
              <BField label="Project type">
                <Select value={form.project_type} onValueChange={(v) => set("project_type", v)}>
                  <SelectTrigger data-testid="bk-project" className="bg-transparent border-black/20 rounded-none">
                    <SelectValue placeholder="Select…" />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-black/15 rounded-none">
                    {PROJECT_TYPES.map((p) => (
                      <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </BField>
              <BField label="Shoot dates">
                <Input data-testid="bk-dates" value={form.shoot_dates} onChange={(e) => set("shoot_dates", e.target.value)} placeholder="e.g. 12–14 April 2026" />
              </BField>
              <BField label="Location">
                <Input data-testid="bk-location" value={form.location} onChange={(e) => set("location", e.target.value)} placeholder="City / country" />
              </BField>
              <BField label="Budget range (EUR)">
                <Input data-testid="bk-budget" value={form.budget_range} onChange={(e) => set("budget_range", e.target.value)} placeholder="e.g. 3 000 – 5 000" />
              </BField>
              <div className="col-span-2">
                <BField label="Brief *">
                  <Textarea
                    data-testid="bk-message"
                    rows={5}
                    value={form.message}
                    onChange={(e) => set("message", e.target.value)}
                    placeholder="Tell us about the project — concept, usage rights, deliverables, exclusivity, etc."
                  />
                </BField>
              </div>
              <div className="col-span-2 flex justify-end gap-3 mt-2">
                <button type="button" onClick={() => setOpen(false)} className="btn-ghost">Cancel</button>
                <button
                  type="submit"
                  data-testid="bk-submit"
                  disabled={submitting}
                  className="btn-primary inline-flex items-center gap-2"
                >
                  {submitting && <Loader2 size={14} className="animate-spin" />} Send inquiry
                </button>
              </div>
              <p className="col-span-2 text-xs text-zinc-500 mt-1 leading-relaxed">
                By submitting, you agree to receive a follow-up email from Ege Models about your inquiry. Your data is never shared with third parties.
              </p>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function BField({ label, children }) {
  return (
    <div>
      <Label className="label-overline text-zinc-700">{label}</Label>
      <div className="mt-2">
        {children}
      </div>
    </div>
  );
}

function SuccessView({ reference, model, onClose }) {
  const copyRef = () => {
    navigator.clipboard.writeText(reference);
    toast.success("Reference copied");
  };
  return (
    <div className="py-6 text-center">
      <div className="mx-auto w-14 h-14 flex items-center justify-center bg-black text-white">
        <CheckCircle2 size={26} />
      </div>
      <h3 className="font-serif text-3xl mt-6 leading-tight">Inquiry sent.</h3>
      <p className="text-zinc-600 mt-3 max-w-md mx-auto leading-relaxed">
        Thank you. Our booking team will reply about <span className="font-medium">{model.name}</span> within 48 hours,
        Monday–Friday.
      </p>
      <div className="mt-6 inline-flex items-center gap-2 border border-black/15 px-4 py-2 font-mono text-sm">
        <span className="label-overline text-zinc-500">Ref</span>
        <span data-testid="bk-success-ref">{reference}</span>
        <button onClick={copyRef} className="text-zinc-500 hover:text-black" aria-label="Copy reference"><Copy size={13} /></button>
      </div>
      <div className="mt-8">
        <button onClick={onClose} className="btn-primary">Close</button>
      </div>
    </div>
  );
}
